import { MonitorAlertFullDto } from "@/app/Alert/types";
import axiosClient, { axiosClientAdmin } from "@/lib/auth";
import { QueryClient, UseInfiniteQueryOptions, UseQueryOptions } from "@tanstack/react-query";
import { SearchInput, SearchInputBase } from "../util/types";
import { AlertEventsSearched, CameraFeedUrlFetched, Comparator, GetSearchAlertEventsAlertMonitoringData, MonitoringAlertsSearched, PostAddNoteMonitoringData, PostSetEventViewedAlertMonitoringData, SortOrder } from "../types.gen";
import { UseMutationOptionsAny } from "../query";

export const fetchAlert = async (alertId: string) => {
  const response = await axiosClient.get<{ alert: MonitorAlertFullDto }>(`/AlertMonitoring/${alertId}/full`);

  return response.data.alert;
}

export const FetchAlertQuery = (alertId: string) => {
  return {
    queryKey: ["alert", alertId],
    queryFn: () => fetchAlert(alertId),
  } satisfies UseQueryOptions
}

export type FetchFullStreamInput = {
  occurred: Date;
  companyId: string;
  projectId: string;
  videoProcessorIdentifier: string;
}

export const fetchFullStream = async ({ occurred, companyId, projectId, videoProcessorIdentifier }: FetchFullStreamInput) => {
  const response = await axiosClient
    .get<CameraFeedUrlFetched>(
      `/company/${companyId}/project/${projectId}/Monitoring/camera/${videoProcessorIdentifier}/feed/${new Date(occurred).toISOString()}`
    )

  return response.data;
}

export const FetchFullStreamQuery = (input: FetchFullStreamInput) => {
  return {
    queryKey: ["fullStream", input],
    queryFn: () => fetchFullStream(input),
  } satisfies UseQueryOptions
}

export type AddAlertNoteInput = PostAddNoteMonitoringData["path"] &
  NonNullable<PostAddNoteMonitoringData["body"]>

export const addAlertNote = async (
  input: AddAlertNoteInput,
) => {
  const { companyId, projectId, alertId, ...body } = input

  return axiosClient.post(
    `/company/${companyId}/project/${projectId}/Monitoring/alerts/${alertId}/note`,
    body
  );
}

export const AddAlertNoteMut = () => {
  return {
    mutationKey: ["addAlertNote"],
    mutationFn: async (input: AddAlertNoteInput) => { await addAlertNote(input) },
  } satisfies UseMutationOptionsAny
}

export const markEventAsViewed = async (alertId: string, eventId: string) => {
  return axiosClient.post(
    `/AlertMonitoring/${alertId}/event/${eventId}/viewed`
  );
}

export const MarkEventAsViewedMut = () => {
  return {
    mutationKey: ["markEventAsViewed"],
    mutationFn: async (input: { alertId: string, eventId: string }) => { await markEventAsViewed(input.alertId, input.eventId) },
  } satisfies UseMutationOptionsAny
}

export const MarkEventAsViewedMut_Optimistic = (
  queryClient: QueryClient,
) => {
  return {
    ...MarkEventAsViewedMut(),
    onMutate: ({ alertId, eventId }: { alertId: string, eventId: string }) => {
      queryClient.setQueryData(
        FetchAlertQuery(alertId).queryKey,
        (oldData: MonitorAlertFullDto | undefined) => {
          if (!oldData) return oldData;

          return {
            ...oldData,
            events: oldData.events?.map((e) => {
              return e.id === eventId ? { ...e, firstViewedAt: new Date().toISOString() } : e;
            }),
          };
        }
      );
    },
    retry: 1,
    onError: ({ alertId }) => {
      queryClient.invalidateQueries({
        queryKey: FetchAlertQuery(alertId).queryKey,
      })
    },
  } satisfies UseMutationOptionsAny

}


export type SearchAlertsInput = SearchInputBase & {
  projectId?: string;
}

export const searchAlerts = async (input: SearchAlertsInput) => {
  const { projectId, ...rest } = input

  const search = new SearchInput({
    ...rest
  })

  if (projectId) {
    search.filterFilters.push({
      field: 'projectId',
      value: projectId,
      action: Comparator.Eq,
    })
  }

  const response = await axiosClient.get<MonitoringAlertsSearched>(
    `/AlertMonitoring/search`,
    { params: search.toParams() }
  )

  return response.data;
}

export const SearchAlertsQuery = (input: SearchAlertsInput) => {
  return {
    queryKey: ["alerts", input],
    queryFn: () => searchAlerts(input),
  } satisfies UseQueryOptions
}

export type SearchAlertsAdminInput = SearchInputBase & {

}

export const searchAlertsAdmin = async (input: SearchAlertsAdminInput) => {
  const { ...rest } = input

  const search = new SearchInput({
    ...rest
  })

  const response = await axiosClientAdmin.get<MonitoringAlertsSearched>(
    `/Monitoring/alerts/search`,
    { params: search.toParams() }
  )

  return response.data;
}

export const SearchAlertsAdminQuery = (input: SearchAlertsAdminInput) => {
  return {
    queryKey: ["admin.alerts", input],
    queryFn: () => searchAlertsAdmin(input),
  } satisfies UseQueryOptions
}


export type SearchAlertEventsInput = GetSearchAlertEventsAlertMonitoringData['path'] &
  SearchInputBase & { after?: Date };

export const searchAlertEvents = async (input: SearchAlertEventsInput) => {
  const pathTpl: GetSearchAlertEventsAlertMonitoringData['url'] =
    '/AlertMonitoring/{alertId}/event/search';
  const path = pathTpl.replace('{alertId}', input.alertId);

  const searchInput = new SearchInput({
    sortBy: 'occurred',
    sortDir: SortOrder.Ascending,
    pageSize: input.pageSize ?? 200,
    pageNumber: input.pageNumber,
  });

  if (input.after) {
    searchInput.filterFilters.push({
      action: Comparator.Gt,
      field: 'occurred',
      value: input.after.toISOString(),
    });
  }

  const params = searchInput.toParams();

  const { data } = await axiosClient.get<AlertEventsSearched>(path, { params });

  return data;
};

export const SearchAlertEventsQuery = (input: SearchAlertEventsInput) => {
  return {
    queryKey: ['alert.events.search', input],
    queryFn: () => searchAlertEvents(input),
  } satisfies UseQueryOptions;
};

export const SearchAlertEventsQueryInfinite = (input: SearchAlertEventsInput) => {
  return {
    queryKey: ['alert.events.search', input],
    queryFn: () => searchAlertEvents(input),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const lastRowCount = lastPage.rows?.length ?? Infinity;
      const pageNum = input.pageSize ?? 200;

      return lastRowCount >= pageNum ? allPages.length + 1 : undefined;
    },
  } satisfies UseInfiniteQueryOptions<AlertEventsSearched, any, any, any, any>;
};